import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ClipboardCheck, AlertTriangle, Users, Bus, TrendingUp, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { getStats, getAllChecks } from '../../lib/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentChecks, setRecentChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, checksRes] = await Promise.all([
        getStats(),
        getAllChecks()
      ]);
      setStats(statsRes.data);
      setRecentChecks(checksRes.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM, HH:mm');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">Dashboard</h1>
          <p className="text-slate-500">Vehicle check overview</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-cards">
          <Card className="stat-card border-l-4 border-l-yellow-500">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-yellow-600">{stats?.total_checks_today || 0}</p>
                <p className="stat-label">Checks Today</p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="stat-card border-l-4 border-l-red-500">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-red-600">{stats?.issues_found_today || 0}</p>
                <p className="stat-label">Issues Today</p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </Card>

          <Card className="stat-card border-l-4 border-l-blue-900">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-blue-900">{stats?.active_drivers || 0}</p>
                <p className="stat-label">Active Drivers</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-900" />
              </div>
            </div>
          </Card>

          <Card className="stat-card border-l-4 border-l-green-600">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value text-green-600">{stats?.active_vehicles || 0}</p>
                <p className="stat-label">Active Vehicles</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Bus className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Weekly Summary */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-900" />
              Weekly Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-3xl font-bold text-blue-900">{stats?.total_checks_week || 0}</div>
              <div className="text-slate-500">total checks this week</div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Checks */}
        <Card className="border-slate-200" data-testid="recent-checks">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Recent Checks</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recentChecks.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No checks submitted yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Driver</th>
                      <th>Vehicle</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentChecks.map((check, idx) => (
                      <tr key={check.id} data-testid={`check-row-${idx}`}>
                        <td className="font-mono text-sm">{formatDate(check.submitted_at)}</td>
                        <td>
                          <div className="font-medium">{check.driver_name}</div>
                          <div className="text-xs text-slate-500">#{check.driver_badge}</div>
                        </td>
                        <td className="font-mono font-semibold">{check.vehicle_registration}</td>
                        <td>
                          {check.has_issues ? (
                            <Badge variant="destructive" className="gap-1">
                              <X className="w-3 h-3" />
                              Issues
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 gap-1">
                              <Check className="w-3 h-3" />
                              Clear
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
